import { lazy, Suspense, useEffect, useMemo, useReducer, useRef } from 'react';
import { Switch, Route, Redirect, useLocation } from 'wouter';
import { AnimatePresence } from 'framer-motion';

import { LazyLoadBoundary, LoadingScreen } from '@shared/ui/layout';
import { AccessRoleSync, PORTALS, managementPaths, portalPaths } from '@modules/shell';
import { getI18nService, type Namespace } from '@infra/i18n';
import { BOOKING_REMINDER_PAGE_PATH, useSyncAllowedLanguages } from '@services';
import { ROUTE_NAMESPACE_MAP } from './constants/router';

const UserDashboard = lazy(() => import('@/pages/user/Dashboard'));
const UserPersonas = lazy(() => import('@/pages/user/Personas'));
const UserPersonaDetail = lazy(() => import('@/pages/user/PersonaDetail'));
const UserPersonaCreate = lazy(() => import('@/pages/user/PersonaCreate'));
const UserScenarios = lazy(() => import('@/pages/user/Scenarios'));
const UserScenarioCreate = lazy(() => import('@/pages/user/ScenarioCreate'));
const UserScenarioDetail = lazy(() => import('@/pages/user/ScenarioDetail'));
const UserRoleplay = lazy(() => import('@/pages/user/Roleplay'));
const UserSessionResolver = lazy(() => import('@/pages/user/Session'));
const UserSessionReport = lazy(() => import('@/pages/user/SessionReport'));
const UserAnalytics = lazy(() => import('@/pages/user/Analytics'));
const UserLearning = lazy(() => import('@/pages/user/Learning'));
const UserProfile = lazy(() => import('@/pages/user/Profile'));
const UserBookings = lazy(() => import('@/pages/user/Bookings'));
const UserBookingReminder = lazy(() => import('@/pages/user/BookingReminder'));
const UserChangePassword = lazy(() => import('@/pages/user/ChangePassword'));
const UserNotifications = lazy(() => import('@/pages/user/Notifications'));
const NotFound = lazy(() => import('@/pages/not-found'));

const AdminSessionDetail = lazy(() => import('@/pages/admin/SessionDetail'));
const AdminDashboard = lazy(() => import('@/pages/admin/Dashboard'));
const AdminUsers = lazy(() => import('@/pages/admin/Users'));
const AdminUserDetail = lazy(() => import('@/pages/admin/UserDetail'));
const AdminPersonas = lazy(() => import('@/pages/admin/Personas'));
const AdminPersonaDetail = lazy(() => import('@/pages/admin/PersonaDetail'));
const AdminPersonaCreate = lazy(() => import('@/pages/admin/PersonaCreate'));
const AdminScenarios = lazy(() => import('@/pages/admin/Scenarios'));
const AdminScenarioDetail = lazy(() => import('@/pages/admin/ScenarioDetail'));
const AdminScenarioCreate = lazy(() => import('@/pages/admin/ScenarioCreate'));
const AdminCoachingHub = lazy(() => import('@/pages/admin/CoachingHub'));
const AdminAnalytics = lazy(() => import('@/pages/admin/Analytics'));
const AdminUsage = lazy(() => import('@/pages/admin/Usage'));
const AdminEntitlements = lazy(() => import('@/pages/admin/Entitlements'));
const AdminSettings = lazy(() => import('@/pages/admin/Settings'));
const AdminChangePassword = lazy(() => import('@/pages/admin/ChangePassword'));
const AdminStages = lazy(() => import('@/pages/admin/Stages'));
const AdminSkills = lazy(() => import('@/pages/admin/Skills'));
const AdminObservableSignals = lazy(() => import('@/pages/admin/ObservableSignals'));
const AdminStageCollection = lazy(() => import('@/pages/admin/StageCollection'));
const AdminSystemConfiguration = lazy(() => import('@/pages/admin/SystemConfiguration'));
const AdminIceServer = lazy(() => import('@/pages/admin/IceServer'));
const AdminBookings = lazy(() => import('@/pages/admin/Bookings'));
const AdminNotifications = lazy(() => import('@/pages/admin/Notifications'));
const AdminOrganizations = lazy(() => import('@/pages/admin/Organizations'));
const AdminOrganizationMember = lazy(() => import('@/pages/admin/OrganizationMember'));
const AdminOrganizationDetail = lazy(() => import('@/pages/admin/OrganizationDetail'));
const AdminOrganizationTeams = lazy(() => import('@/pages/admin/OrganizationTeams'));
const AdminOrganizationTeamDetail = lazy(() => import('@/pages/admin/OrganizationTeamDetail'));
const OrgDashboard = lazy(() => import('@/pages/org/Dashboard'));
const OrgOrganizationMember = lazy(() => import('@/pages/org/OrganizationMember'));
const OrgOrganization = lazy(() => import('@/pages/org/Organization'));
const OrgCoachingHub = lazy(() => import('@/pages/org/CoachingHub'));
const OrgSessionDetail = lazy(() => import('@/pages/org/SessionDetail'));
const OrgAnalytics = lazy(() => import('@/pages/org/Analytics'));
const OrgBookings = lazy(() => import('@/pages/org/Bookings'));
const OrgEntitlements = lazy(() => import('@/pages/org/Entitlements'));
const OrgOrganizationTeamDetail = lazy(() => import('@/pages/org/OrganizationTeamDetail'));
const OrgUsage = lazy(() => import('@/pages/org/Usage'));
const OrgPersonas = lazy(() => import('@/pages/org/Personas'));
const OrgPersonaDetail = lazy(() => import('@/pages/org/PersonaDetail'));
const OrgPersonaCreate = lazy(() => import('@/pages/org/PersonaCreate'));
const OrgScenarios = lazy(() => import('@/pages/org/Scenarios'));
const OrgScenarioDetail = lazy(() => import('@/pages/org/ScenarioDetail'));
const OrgScenarioCreate = lazy(() => import('@/pages/org/ScenarioCreate'));

// /org/* is gated once, by RequireAccess around this router in App.tsx.
const UserCoachingHub = lazy(() => import('@/pages/user/CoachingHub'));
const UserCoachingSession = lazy(() => import('@/pages/user/CoachingSession'));
const UserMyTeam = lazy(() => import('@/pages/user/MyTeam'));
const UserUsage = lazy(() => import('@/pages/user/Usage'));
const UserMyTeamMember = lazy(() => import('@/pages/user/MyTeamMember'));

const i18nService = getI18nService();

/**
 * Gets i18n namespaces required for a given route path
 * @param path - Current route path
 * @returns Array of namespace strings
 * @internal
 */
function getNamespacesForRoute(path: string): Namespace[] {
  const match = ROUTE_NAMESPACE_MAP.find((entry) => entry.test(path));
  return match ? Array.from(new Set(match.namespaces)) : [];
}

/**
 * Main application router component
 *
 * Handles:
 * - Route-based code splitting with lazy loading
 * - i18n namespace preloading for routes
 * - Error boundaries for lazy-loaded components
 *
 * @returns Router component with all application routes
 */
export function Router() {
  const [location] = useLocation();
  const namespaces = useMemo(() => getNamespacesForRoute(location), [location]);
  const nsKey = namespaces.join(',');

  // Runs once for the whole app; the shared pickers know nothing of entitlements.
  useSyncAllowedLanguages();

  // Re-render trigger for when an async namespace load finishes (or gives up).
  const [, forceRerender] = useReducer((x: number) => x + 1, 0);
  // Route namespaces we gave up loading (after retries) → render anyway with fallback lang.
  const gaveUpKeyRef = useRef<string | null>(null);

  // Derive readiness SYNCHRONOUSLY during render (no setState(false)) so returning to a
  // route whose namespaces are already cached renders instantly with ZERO loader flash.
  // Under React 18 concurrent (createRoot), a setState(false) in an effect commits before
  // paint while the matching setState(true) from the microtask-resolved preload is deferred
  // to a Scheduler MessageChannel task that runs AFTER paint → a 1-frame loader flash.
  const namespacesReady =
    i18nService.hasNamespacesLoaded(namespaces) || gaveUpKeyRef.current === nsKey;

  useEffect(() => {
    if (i18nService.hasNamespacesLoaded(namespaces)) return; // already cached → nothing to do

    let isActive = true;
    let retryCount = 0;
    const MAX_RETRIES = 3;
    const RETRY_DELAY_MS = 1000;

    const loadNamespaces = async () => {
      try {
        await i18nService.preloadNamespaces(namespaces);
        if (isActive) forceRerender(); // hasNamespacesLoaded() flips true → renders content
      } catch (error) {
        console.error('Failed to preload namespaces:', error);

        // Retry logic with exponential backoff
        if (retryCount < MAX_RETRIES && isActive) {
          retryCount++;
          setTimeout(() => {
            if (isActive) loadNamespaces();
          }, RETRY_DELAY_MS * retryCount);
        } else if (isActive) {
          // Give up → render anyway with fallback language
          console.warn('Using default language due to namespace loading failure');
          gaveUpKeyRef.current = nsKey;
          forceRerender();
        }
      }
    };

    loadNamespaces();

    return () => {
      isActive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- keyed by nsKey (stable string of `namespaces`)
  }, [nsKey]);

  return (
    <>
      <AccessRoleSync />
      {!namespacesReady ? (
        <LoadingScreen />
      ) : (
        <LazyLoadBoundary>
          <Suspense fallback={<LoadingScreen />}>
            <AnimatePresence mode="wait">
              <Switch>
                <Route path="/admin">
                  <Redirect to={managementPaths.home('admin')} />
                </Route>
                <Route path="/user">
                  <Redirect to={managementPaths.home('user')} />
                </Route>

                <Route path={managementPaths.home('user')} component={UserDashboard} />
                <Route path="/user/personas/new" component={UserPersonaCreate} />
                <Route path="/user/personas/:uuid/edit" component={UserPersonaCreate} />
                <Route path="/user/personas/:uuid" component={UserPersonaDetail} />
                <Route path="/user/personas" component={UserPersonas} />
                <Route path="/user/scenarios/new" component={UserScenarioCreate} />
                <Route path="/user/scenarios/:id/edit" component={UserScenarioCreate} />
                <Route path="/user/scenarios/:id" component={UserScenarioDetail} />
                <Route path="/user/scenarios" component={UserScenarios} />
                <Route path="/user/roleplay/:sessionId" component={UserRoleplay} />
                <Route path="/user/sessions/:id/report" component={UserSessionReport} />
                <Route path="/user/sessions/:id" component={UserSessionResolver} />
                <Route path="/user/analytics" component={UserAnalytics} />
                <Route path="/user/coaching-hub/:id" component={UserCoachingSession} />
                <Route path="/user/coaching-hub" component={UserCoachingHub} />
                <Route path="/user/team/members/:uuid" component={UserMyTeamMember} />
                <Route path="/user/team" component={UserMyTeam} />
                <Route path="/user/usage" component={UserUsage} />
                <Route path="/user/learning" component={UserLearning} />
                <Route path={`${BOOKING_REMINDER_PAGE_PATH}:uuid`} component={UserBookingReminder} />
                <Route path={portalPaths(PORTALS.user.basePath).bookings} component={UserBookings} />
                <Route path="/user/notifications" component={UserNotifications} />
                <Route path="/user/profile" component={UserProfile} />
                <Route path="/user/change-password" component={UserChangePassword} />

                {/* The admin screens answer on the plain paths too, the same
                promotion the user side already made. */}
                <Route path="/admin/dashboard" component={AdminDashboard} />
                {/* `/new` and `/import` must precede `/:id`, or wouter matches them as ids. */}
                <Route path="/admin/users/new">
                  <Redirect to="/admin/users?form=create" replace />
                </Route>
                <Route path="/admin/users/import">
                  <Redirect to="/admin/users?form=import" replace />
                </Route>
                <Route path="/admin/users/:uuid" component={AdminUserDetail} />
                <Route path="/admin/users" component={AdminUsers} />
                <Route path="/admin/personas/new" component={AdminPersonaCreate} />
                <Route path="/admin/personas/:uuid/edit" component={AdminPersonaCreate} />
                <Route path="/admin/personas/:uuid" component={AdminPersonaDetail} />
                <Route path="/admin/personas" component={AdminPersonas} />
                <Route path="/admin/scenarios/new" component={AdminScenarioCreate} />
                <Route path="/admin/scenarios/:id/edit" component={AdminScenarioCreate} />
                <Route path="/admin/scenarios/:id" component={AdminScenarioDetail} />
                <Route path="/admin/scenarios" component={AdminScenarios} />
                <Route path="/admin/coaching-hub/:id" component={AdminSessionDetail} />
                <Route path="/admin/coaching-hub" component={AdminCoachingHub} />
                <Route path="/admin/analytics" component={AdminAnalytics} />
                <Route path="/admin/usage" component={AdminUsage} />
                <Route path="/admin/entitlements" component={AdminEntitlements} />
                <Route
                  path="/admin/organizations/:uuid/members/:userUuid"
                  component={AdminOrganizationMember}
                />
                <Route
                  path="/admin/organizations/:uuid/teams/:teamUuid"
                  component={AdminOrganizationTeamDetail}
                />
                <Route path="/admin/organizations/:uuid/teams" component={AdminOrganizationTeams} />
                <Route path="/admin/organizations/:uuid" component={AdminOrganizationDetail} />
                <Route path="/admin/organizations" component={AdminOrganizations} />
                <Route path="/admin/settings/stage-collection" component={AdminStageCollection} />
                <Route path="/admin/settings/stages" component={AdminStages} />
                <Route path="/admin/settings/skills" component={AdminSkills} />
                <Route
                  path="/admin/settings/observable-signals"
                  component={AdminObservableSignals}
                />
                <Route
                  path="/admin/settings/system-configuration"
                  component={AdminSystemConfiguration}
                />
                <Route path="/admin/settings/ice-server" component={AdminIceServer} />
                <Route path="/admin/settings" component={AdminSettings} />
                <Route path={managementPaths.bookings('admin')} component={AdminBookings} />
                <Route path="/admin/change-password" component={AdminChangePassword} />
                <Route path="/admin/notifications" component={AdminNotifications} />

                <Route path="/org">
                  <Redirect to="/org/dashboard" />
                </Route>
                <Route path="/org/dashboard" component={OrgDashboard} />
                <Route path="/org/organization/members/:userUuid" component={OrgOrganizationMember} />
                <Route path="/org/organization/teams/:teamUuid" component={OrgOrganizationTeamDetail} />
                <Route path="/org/organization" component={OrgOrganization} />
                <Route path="/org/analytics" component={OrgAnalytics} />
                <Route path="/org/usage" component={OrgUsage} />
                <Route path="/org/entitlements" component={OrgEntitlements} />
                <Route path="/org/profile" component={UserProfile} />
                <Route path="/org/notifications" component={UserNotifications} />
                <Route path="/org/change-password" component={UserChangePassword} />
                {/* Old org admin URLs land on the organization page. */}
                <Route path="/org/users/new">
                  <Redirect to={managementPaths.organization('org', '')} replace />
                </Route>
                <Route path="/org/users/import">
                  <Redirect to={managementPaths.organization('org', '')} replace />
                </Route>
                <Route path="/org/users/:id">
                  {(params) => <Redirect to={managementPaths.member('org', '', params.id)} replace />}
                </Route>
                <Route path="/org/users">
                  <Redirect to={managementPaths.organization('org', '')} replace />
                </Route>
                <Route path="/org/teams/:uuid">
                  {(params) => <Redirect to={managementPaths.team('org', '', params.uuid)} replace />}
                </Route>
                <Route path="/org/teams">
                  <Redirect to={managementPaths.organization('org', '', 'teams')} replace />
                </Route>
                <Route path="/org/settings">
                  <Redirect to={managementPaths.organization('org', '')} replace />
                </Route>
                <Route path="/org/coaching-hub/:id" component={OrgSessionDetail} />
                <Route path="/org/coaching-hub" component={OrgCoachingHub} />
                <Route path="/org/scenarios/new" component={OrgScenarioCreate} />
                <Route path="/org/scenarios/:id/edit" component={OrgScenarioCreate} />
                <Route path="/org/scenarios/:id" component={OrgScenarioDetail} />
                <Route path="/org/scenarios" component={OrgScenarios} />
                <Route path="/org/personas/new" component={OrgPersonaCreate} />
                <Route path="/org/personas/:uuid/edit" component={OrgPersonaCreate} />
                <Route path="/org/personas/:uuid" component={OrgPersonaDetail} />
                <Route path="/org/personas" component={OrgPersonas} />
                <Route path={managementPaths.bookings('org')} component={OrgBookings} />

                <Route path="/404" component={NotFound} />

                <Route>
                  <Redirect to="/404" />
                </Route>
              </Switch>
            </AnimatePresence>
          </Suspense>
        </LazyLoadBoundary>
      )}
    </>
  );
}
